import React, { useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { NoticeModel, MemberModel, NOTICE_CATEGORIES } from '../../../../models';
import { db } from '../../../../firebase';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { ArrowLeft, Save, X, Info } from 'lucide-react';

interface NoticeEditorProps {
    notice: NoticeModel | null;
    currentAdmin: MemberModel | null;
    onClose: () => void;
}

const NoticeEditor: React.FC<NoticeEditorProps> = ({ notice, currentAdmin, onClose }) => {
    const [title, setTitle] = useState(notice?.title || '');
    const [content, setContent] = useState(notice?.content || '');
    const [category, setCategory] = useState<NoticeModel['category']>(notice?.category || '학원공지');
    const [targetAudience, setTargetAudience] = useState<NoticeModel['targetAudience']>(notice?.targetAudience || ['student']);
    const [branchId, setBranchId] = useState<string>(notice?.branchId || 'all');
    const [branches, setBranches] = useState<any[]>([]); // Using any[] to avoid missing import, or import BranchModel if available
    const [isPinned, setIsPinned] = useState(notice?.isPinned || false);
    const [isSaving, setIsSaving] = useState(false);

    const [categories, setCategories] = useState<string[]>(NOTICE_CATEGORIES as any);

    // Fetch branches for super admin & Site Settings
    React.useEffect(() => {
        const fetchInit = async () => {
            // Fetch Site Settings for Categories
            const settingSnap = await import('firebase/firestore').then(mod => mod.getDoc(mod.doc(db, 'settings', 'site')));
            if (settingSnap.exists()) {
                const data = settingSnap.data() as any;
                if (data.noticeCategories && data.noticeCategories.length > 0) {
                    setCategories(data.noticeCategories);
                } else {
                    setCategories(NOTICE_CATEGORIES as any);
                }
            }

            if (currentAdmin?.role === 'super_admin') {
                const querySnapshot = await import('firebase/firestore').then(mod => mod.getDocs(mod.collection(db, 'branches')));
                const list: any[] = [];
                querySnapshot.forEach(doc => list.push({ id: doc.id, ...doc.data() }));
                setBranches(list);
            }
        };
        fetchInit();
    }, [currentAdmin]);

    const handleSave = async () => {
        if (!title.trim() || !content.trim()) {
            alert('제목과 내용을 입력해주세요.');
            return;
        }

        setIsSaving(true);
        try {
            const noticeData = {
                title,
                content,
                category,
                targetAudience: (currentAdmin?.role === 'super_admin') ? targetAudience : ['student'],
                branchId: (currentAdmin?.role === 'super_admin') ? branchId : (currentAdmin?.managedBranchId || currentAdmin?.branchId || 'all'),
                isPinned,
                updatedAt: Date.now(),
            };

            if (notice) {
                await updateDoc(doc(db, 'notices', notice.id), noticeData);
            } else {
                await addDoc(collection(db, 'notices'), {
                    ...noticeData,
                    createdAt: Date.now(),
                    viewCount: 0,
                    authorName: currentAdmin?.name || '관리자',
                    branchId: (currentAdmin?.role === 'super_admin') ? (notice?.branchId || 'all') : (currentAdmin?.managedBranchId || currentAdmin?.branchId || 'all'),
                });
            }
            onClose();
        } catch (error) {
            console.error('Error saving notice:', error);
            alert('저장 중 오류가 발생했습니다.');
        } finally {
            setIsSaving(false);
        }
    };



    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
        ],
    };

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Toolbar */}
            <div className="bg-white border-b border-slate-200 px-8 py-4 flex justify-between items-center sticky top-0 z-10">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                    >
                        <ArrowLeft size={24} className="text-slate-600" />
                    </button>
                    <h1 className="text-xl font-bold text-slate-900">
                        {notice ? '공지사항 수정' : '새 공지사항 작성'}
                    </h1>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 text-slate-600 hover:bg-slate-50 font-semibold rounded-xl transition-all"
                    >
                        취소
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white px-6 py-2.5 rounded-xl font-bold flex items-center space-x-2 transition-all shadow-lg shadow-indigo-200"
                    >
                        {isSaving ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <Save size={20} />
                        )}
                        <span>{notice ? '수정사항 저장' : '공지사항 등록'}</span>
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-8 bg-slate-50/30">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Title & Meta */}
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 space-y-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">제목</label>
                            <input
                                type="text"
                                placeholder="공지사항 제목을 입력해주세요"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-5 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-500 transition-all text-lg font-medium"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">카테고리</label>
                                <div className="flex flex-wrap gap-2">
                                    {categories.map((cat) => (
                                        <button
                                            key={cat}
                                            onClick={() => setCategory(cat)}
                                            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${category === cat
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {currentAdmin?.role === 'super_admin' && (
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">지점 설정</label>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setBranchId('all')}
                                            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${branchId === 'all'
                                                ? 'bg-slate-800 text-white border-slate-800'
                                                : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                                                }`}
                                        >
                                            전체 지점
                                        </button>
                                        {branches.map(branch => (
                                            <button
                                                key={branch.id}
                                                type="button"
                                                onClick={() => setBranchId(branch.id)}
                                                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${branchId === branch.id
                                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                                    : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                                                    }`}
                                            >
                                                {branch.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">공지 대상</label>
                                {currentAdmin?.role === 'super_admin' ? (
                                    <div className="flex gap-2 w-full">
                                        {(['student', 'teacher'] as const).map((aud) => (
                                            <button
                                                key={aud}
                                                type="button"
                                                onClick={() => {
                                                    // @ts-ignore
                                                    setTargetAudience(prev =>
                                                        prev.includes(aud)
                                                            ? prev.filter(a => a !== aud)
                                                            : [...prev, aud]
                                                    );
                                                }}
                                                className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all border ${targetAudience.includes(aud)
                                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                                                    : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
                                                    }`}
                                            >
                                                {aud === 'student' ? '학생' : '선생님'}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="w-full flex items-center space-x-2 px-4 py-3 bg-slate-100 text-slate-500 rounded-xl border border-slate-200">
                                        <span className="text-sm font-bold">학생 전용 (선생님은 학생 공지사항만 작성 가능)</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <label className="flex items-center space-x-3 cursor-pointer group">
                            <div className="relative">
                                <input
                                    type="checkbox"
                                    checked={isPinned}
                                    onChange={(e) => setIsPinned(e.target.checked)}
                                    className="sr-only p-2"
                                />
                                <div className={`w-12 h-6 rounded-full transition-colors ${isPinned ? 'bg-indigo-600' : 'bg-slate-200'}`} />
                                <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform ${isPinned ? 'translate-x-6' : ''}`} />
                            </div>
                            <span className="text-slate-700 font-bold">공지사항 상담 고정</span>
                        </label>
                    </div>

                    {/* Editor */}
                    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden min-h-[500px] flex flex-col">
                        <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center text-slate-500 text-sm">
                            <Info size={16} className="mr-2" />
                            상세 내용을 입력하세요. 이미지와 링크를 포함할 수 있습니다.
                        </div>
                        <div className="flex-1 flex flex-col p-6">
                            <ReactQuill
                                theme="snow"
                                value={content}
                                onChange={setContent}
                                modules={quillModules}
                                className="flex-1 flex flex-col h-full"
                                placeholder="여기에 내용을 입력하세요..."
                            />
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .quill {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }
                .ql-container {
                    flex: 1;
                    font-size: 16px;
                    border: none !important;
                }
                .ql-toolbar {
                    border: none !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                    padding: 8px 0 !important;
                }
                .ql-editor {
                    padding: 20px 0 !important;
                }
                .ql-editor.ql-blank::before {
                    left: 0 !important;
                    font-style: normal !important;
                    color: #94a3b8 !important;
                }
            `}</style>
        </div>
    );
};

export default NoticeEditor;
